import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { environment } from '../../environments/environment';
import { PROJECT_NAME, ProjectName, ProjectNames } from '../tokens';

export interface AxonEventData {
  item: string;
  transaction_id?: string;
  value: number;
  currency: string;
}

/**
 * Service for Axon (AppLovin) pixel tracking
 * Documentation: https://support.axon.ai/en/growth/promoting-your-websites/axon-pixel-integration/events-and-objects
 */
@Injectable({
  providedIn: 'root',
})
export class AxonService {
  private isInitialized = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(PROJECT_NAME) private projectName?: ProjectName
  ) {}

  init(): void {
    if (this.isInitialized || !this.isEnabledProject() || typeof window === 'undefined') {
      return;
    }

    this.isInitialized = true;

    const targetWindow = window as Window &
      typeof globalThis & {
        axon?: any;
      };

    if (!targetWindow.axon) {
      const axonQueue = function (...args: unknown[]) {
        if (axonQueue.performOperation) {
          axonQueue.performOperation.apply(axonQueue, args);
        } else {
          axonQueue.operationQueue.push(args);
        }
      } as any;

      axonQueue.operationQueue = [];
      axonQueue.ts = Date.now();
      // Real key only in prod; non-prod builds get a deliberately wrong key from the environment
      axonQueue.eventKey = environment.axonEventKey;
      targetWindow.axon = axonQueue;

      for (const scriptUrl of [
        'https://s.axon.ai/pixel.js',
        'https://res4.applovin.com/p/l/loader.iife.js',
      ]) {
        if (this.document.querySelector(`script[src="${scriptUrl}"]`)) {
          continue;
        }

        const script = this.document.createElement('script');
        script.async = true;
        script.src = scriptUrl;

        const firstScript = this.document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          this.document.head.appendChild(script);
        }
      }
    }

    targetWindow.axon('init');
  }

  /**
   * Tracks page view event - should fire on every page/step
   */
  pageView(): void {
    this.track('page_view');
  }

  login(email: string): void {
    this.track('login', {
      user_data: {
        email: email.toLowerCase(),
      },
    });
  }

  /**
   * Tracks view_item event - should fire on paywall and when user changes plan
   */
  viewItem(data: AxonEventData): void {
    this.track('view_item', {
      items: [this.getItemData(data)],
      value: data.value,
      currency: data.currency,
    });
  }

  /**
   * Tracks begin_checkout event - should fire when entering checkout and adding to cart - in web funnels they are fired at the same time
   */
  beginCheckout(data: AxonEventData): void {
    this.track('add_to_cart', {
      items: [this.getItemData(data)],
      value: data.value,
      currency: data.currency,
    });

    this.track('begin_checkout', {
      items: [this.getItemData(data)],
      value: data.value,
      currency: data.currency,
    });
  }

  /**
   * Tracks purchase event - should fire on payment confirmation
   */
  purchase(data: AxonEventData): void {
    this.track('purchase', {
      items: [this.getItemData(data)],
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
    });
  }

  private track(eventName: string, eventData?: Record<string, unknown>): void {
    try {
      if (typeof axon === 'function') {
        axon('track', eventName, eventData);
      }
    } catch (error) {
      console.error('Axon tracking error:', error);
    }
  }

  private getItemData(data: AxonEventData): Record<string, string | number> {
    return {
      item_id: data.item,
      quantity: 1,
      price: data.value,
      item_category_id: 543541,
      image_url: 'https://getpeech.com/logo-icon.png',
      item_name: this.getItemName(data.item),
      item_variant_id: this.getProductVariantId(data.item),
    };
  }

  private getItemName(productCode: string): string {
    if (productCode.includes('premium')) {
      return 'Subscription';
    } else if (productCode.includes('addon')) {
      return 'Upsell';
    }

    return 'Peech Product';
  }

  private getProductVariantId(productCode: string): string {
    if (productCode.includes('12weeks.premium.notial')) {
      return 'subscription_quarterly';
    } else if (productCode.includes('4weeks.premium.notial')) {
      return 'subscription_monthly';
    } else if (productCode.includes('4weeks.premium.trial-7')) {
      return 'subscription_monthly_with_trial';
    } else if (productCode.includes('addon')) {
      return this.extractAddonSlug(productCode) || 'peech_upsell';
    }

    return 'peech_product';
  }

  private extractAddonSlug(code: string): string | null {
    if (!code) {
      return null;
    }

    const parts = code.split('.');

    return parts.length >= 3 ? parts[2] : null;
  }

  private isEnabledProject(): boolean {
    const projectName = this.projectName ?? ProjectNames.FUNNEL;

    return (
      projectName === ProjectNames.FUNNEL ||
      projectName === ProjectNames.GOOGLE_FUNNEL ||
      projectName === ProjectNames.APPLOVIN_FUNNEL ||
      projectName === ProjectNames.SHORT_FUNNEL ||
      projectName === ProjectNames.KINDLE_FUNNEL ||
      projectName === ProjectNames.ROMANCE_FUNNEL ||
      projectName === ProjectNames.READER_FUNNEL
    );
  }
}
